import { lazy, Suspense, useEffect, type ReactNode } from "react";
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
} from "react-router";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Toaster } from "@bindery/ui/components/ui/sonner";
import { AdPrivacyNotice } from "@/components/ad-privacy-notice";
import { AppNav } from "@/components/app-nav";
import { LibraryPage } from "@/reader/library-page";
import { TeacherPage } from "@/classroom/teacher-page";
import { AuthCallback } from "@/auth/auth-callback";
import { InstallOffer } from "@/install/InstallOffer";
import { EngagementTracker } from "@/lib/engagement/engagement-tracker";
import { titleFor } from "@/prerender-manifest";

/**
 * WHAT IS IN THE ENTRY, AND WHY.
 *
 * Everything this file imports statically is in the chunk every visitor
 * downloads and evaluates before anything paints, on every route. That used
 * to be every page in the app: a click on a `/for-teachers/odyssey` ad paid
 * for the reader's panes, its gloss popover, its selection layer and its
 * markdown flattener before it could render a word of the pitch it was sold.
 *
 * The split is by ARRIVAL, not by size. Two routes are landing pages: `/`
 * (the shelf) and `/for-teachers/odyssey` (the classroom pitch, where the
 * paid traffic lands). Their components are eager because a landing page's
 * first paint IS its arrival — there is no warm entry to hide a chunk fetch
 * behind.
 *
 * Both are prerendered (src/prerender-manifest.ts). `/` is written to
 * `home.html` and served by a `_redirects` rule, not into `index.html`,
 * which is also the SPA fallback every other route gets.
 *
 * WHAT THE LAZY ROUTES COST, HONESTLY. For `/subscribe`, `/about` and the
 * admin surface the premise holds: they are reached by a navigation that
 * already has the entry warm, so a chunk fetch lands while the app is on
 * screen rather than while it is blank.
 *
 * `/read/:bookSlug/:unit` is the exception, and it is the URL shape that gets
 * bookmarked, shared, and linked from a classroom. Served the SPA fallback,
 * whose modulepreload list is the ENTRY graph, a cold arrival went HTML ->
 * entry -> evaluate -> match the route -> fetch `reading-view` and its chunks
 * -> fetch the corpus catalog, serially. So public/_redirects rewrites
 * `/read/*` onto `read-shell.html` (scripts/route-shells.ts): index.html plus
 * that closure and the catalog as preloads, which then download alongside
 * the entry. They are not simply in index.html because that document also
 * serves `/`, `/admin/*` and `/classroom/:token`, which do not read.
 *
 * `ReaderPrefetch` below narrows this to cold arrivals only: it warms the
 * reader chunk when the visitor is on `/`, so shelf -> reader pays nothing.
 *
 * `AuthCallback` stays eager for the same arrival reason as the landing
 * pages, in the other direction: it is the first paint after a sign-in
 * redirect, and it renders one `<Navigate>` — a chunk fetch to answer a
 * redirect would cost more than the module weighs.
 */
const ReadingView = lazy(() =>
  import("@/reader/reading-view").then((m) => ({ default: m.ReadingView })),
);
const SubscribePage = lazy(() =>
  import("@/subscribe/subscribe-page").then((m) => ({
    default: m.SubscribePage,
  })),
);
const PurchaseComplete = lazy(() =>
  import("@/purchase/complete").then((m) => ({ default: m.PurchaseComplete })),
);
const PurchaserPage = lazy(() =>
  import("@/classroom/purchaser-page").then((m) => ({
    default: m.PurchaserPage,
  })),
);
const AboutPage = lazy(() =>
  import("@/about/about-page").then((m) => ({ default: m.AboutPage })),
);
const PrivacyPage = lazy(() =>
  import("@/about/privacy-page").then((m) => ({ default: m.PrivacyPage })),
);
const ClaimPage = lazy(() =>
  import("@/claim/claim-page").then((m) => ({ default: m.ClaimPage })),
);

// Lazy so the admin console (and, soon, its Shiki/tokenizer weight) never
// ships in the anonymous reader's bundle — an anonymous reader's path never
// touches Convex (@/lib/corpus.ts:1-5) and shouldn't pay for the console that
// does. (`AppNav` asks Convex for `admins.myRole`, but only once a session is
// confirmed, so the anonymous reading path is still Convex-free.)
const AdminPage = lazy(() =>
  import("@/admin/admin-page").then((m) => ({ default: m.AdminPage })),
);

/**
 * The gap between a route matching and its chunk arriving.
 *
 * Deliberately the same mark the app already uses for a page it cannot draw
 * yet (`AdminPanel`'s "Checking access…", `WorkbenchShell`): one muted
 * `Loader2` on a full-height field, nothing else. A skeleton would be worse
 * here — it would have to guess at a layout it does not own, and a wrong
 * guess reflows the moment the real page lands.
 *
 * `aria-busy` and the visually-hidden label are what a screen reader gets;
 * the spinner itself is decoration and is hidden from the tree.
 */
function RouteFallback() {
  return (
    <main
      aria-busy="true"
      className="mx-auto flex min-h-svh max-w-md flex-col items-center justify-center gap-5 px-6 text-center"
    >
      <Loader2
        aria-hidden="true"
        className="text-muted-foreground size-6 animate-spin"
      />
      <span className="sr-only">Loading…</span>
    </main>
  );
}

/**
 * `RouteFallback` plus the floating nav, for the routes that are NOT under
 * `ChromeLayout` and so have no site controls of their own until their chunk
 * lands. The reader is excluded on purpose: it hangs `AppNav` inside its own
 * toolbar, so drawing a floating one here would move it on arrival.
 */
function ChromelessFallback() {
  return (
    <>
      <AppNav variant="floating" />
      <RouteFallback />
    </>
  );
}

/**
 * The one speculative fetch in the app: on the shelf, once the browser is
 * idle, pull the reader chunk that every cover on the page links to.
 *
 * Lazy routes trade a smaller entry for a serial hop — the chunk is not
 * discovered until the route matches, which on a cover press is the worst
 * possible moment. Warming it from `/` moves that hop into time the visitor
 * is spending reading the shelf. It is scoped to `/` rather than run
 * everywhere for the reason the split exists at all: `/for-teachers/odyssey`
 * is an ad landing page and must not spend a paid visitor's bytes on the
 * reader, idle or not.
 *
 * `requestIdleCallback` is not in the Safari end of BROWSER_FLOOR
 * (packages/config/vite.ts), so the timeout is the floor's path, not a
 * fallback for an exotic browser.
 */
function ReaderPrefetch() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (pathname !== "/") return;
    let cancelled = false;
    const warm = () => {
      if (!cancelled) void import("@/reader/reading-view");
    };
    if (typeof requestIdleCallback === "function") {
      const handle = requestIdleCallback(warm, { timeout: 3000 });
      return () => {
        cancelled = true;
        cancelIdleCallback(handle);
      };
    }
    const timer = setTimeout(warm, 1500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [pathname]);
  return null;
}

/**
 * Keeps `document.title` in step with the route.
 *
 * The prerender pass writes each manifest route's title into its own document
 * (src/prerender-manifest.ts), which is what a crawler and a shared link see.
 * A client-side navigation changes neither, so without this the title a
 * visitor landed under would follow them across the whole site — arriving on
 * the teachers page and pressing through to the shelf would leave "The
 * Odyssey for the classroom" over the library.
 *
 * An effect rather than render-time assignment: the title is a document
 * mutation, not part of this component's output, and doing it during render
 * would fire inside the build-time render, where there is no document at all.
 */
function DocumentTitle() {
  const { pathname } = useLocation();
  useEffect(() => {
    document.title = titleFor(pathname);
  }, [pathname]);
  return null;
}

/**
 * The shell for pages that have no header of their own: it hangs `AppNav` in
 * the top-right corner above the matched page.
 *
 * Surfaces that DO own a header are deliberately NOT under this layout — the
 * reader and the membership page render `<AppNav variant="inline">` inside
 * their own toolbar instead, so the site controls are part of that row rather
 * than floating over it. That overlap (and the `pr-16` reservations papering
 * over it) is what this split exists to end.
 *
 * The `Suspense` is here rather than per-route because this layout is exactly
 * the set of pages whose loading state is "nav, then a field" — one boundary
 * covers every one of them, and the two eager routes under it (`/` and
 * `/for-teachers/odyssey`) never suspend, so it costs them nothing.
 */
function ChromeLayout() {
  return (
    <>
      <AppNav variant="floating" />
      <Suspense fallback={<RouteFallback />}>
        <Outlet />
      </Suspense>
    </>
  );
}

function App({ authError }: { authError: string | null }) {
  // `authError` is captured at boot (main.tsx), before the app renders, so a
  // failed exchange survives even if the matched route (`/auth/callback`)
  // immediately navigates the URL elsewhere (review MINOR finding 6). It fires
  // HERE, above the router, so that crossing between the two layout groups
  // below can never remount the effect and toast the same failure twice.
  useEffect(() => {
    if (authError !== null) {
      toast.error("Sign-in failed", { description: authError });
    }
  }, [authError]);

  return (
    <AppChrome>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AppChrome>
  );
}

/**
 * Everything the browser entry and the build-time prerender pass both render
 * AROUND the router, given one.
 *
 * It is a component rather than two copies of the same JSX because it is not
 * empty: `<Toaster />` emits a real `<section>` even with no toasts in it
 * (measured — `renderToString(<Toaster />)` is a 160-byte element, not ""), so
 * a prerendered document that skipped it would differ from the tree the client
 * builds. React happens to tolerate THAT particular difference, a trailing
 * node at the end of the container (measured too, by deleting it from
 * src/prerender-hydration.test.tsx's client tree and watching the test stay
 * green) — which is exactly why one composition is worth more than a comment
 * asking two of them to agree. The next thing added here would not be at the
 * end, and would not be tolerated.
 */
export function AppChrome({ children }: { children: ReactNode }) {
  return (
    <>
      {children}
      <Toaster />
      {/* Renders nothing on the server and on the first client render — the
          offer decides in an effect (src/install/) — so it costs the
          prerendered markup nothing. Here, outside the router, because it
          counts document loads and must not remount on navigation. */}
      <InstallOffer />
    </>
  );
}

/**
 * The routed tree, below whichever router mounted it: `BrowserRouter` in the
 * browser, `StaticRouter` in scripts/prerender.tsx. Exported for that second
 * caller — see `AppChrome` for why the two must not diverge.
 */
export function AppRoutes() {
  return (
    <>
      {/* Inside the router so it can watch navigation, outside `Routes` so a
          route change does not remount it. Renders nothing. */}
      <EngagementTracker />
      {/* Renders nothing; fires the one-time ad-arrival privacy disclosure
          once the tracker above has a server-acknowledged tier stored (the
          ack lands async, so the toast shows on the following navigation). */}
      <AdPrivacyNotice />
      {/* Renders nothing; warms the reader chunk from the shelf. */}
      <ReaderPrefetch />
      {/* Renders nothing; keeps document.title in step with the route. */}
      <DocumentTitle />
      <Routes>
        {/* Headers of their own — see `ChromeLayout`. `:unit` is a chapter
              number (all digits) or a matter slug (PD-2); ReadingView
              dispatches on the shape. */}
        <Route
          path="/read/:bookSlug/:unit"
          element={
            // Bare `RouteFallback`, no floating nav: the reader hangs
            // `AppNav variant="inline"` in its own toolbar, so a floating
            // one drawn here would jump across the page on arrival.
            <Suspense fallback={<RouteFallback />}>
              <ReadingView />
            </Suspense>
          }
        />
        {/* Plans + the reader's own membership, one page: arriving from a
              spent allowance or the paywall, the question is always both
              halves at once. */}
        <Route
          path="/subscribe"
          element={
            // Same reason as the reader: this page owns its toolbar.
            <Suspense fallback={<RouteFallback />}>
              <SubscribePage />
            </Suspense>
          }
        />
        {/* Both segments optional: bare `/admin` still works and resolves to
              the first configured book's first unit, while
              `/admin/peter-pan/3?loc=b012` is a shareable deep link straight to
              a paragraph. `:unit` has the same shape as the reader's — a
              chapter number or a matter slug — and both routes resolve it
              through `convex/model/unitOrder.ts`.

              Out here rather than under `ChromeLayout` because the two
              workbenches behind this route own full-height headers of their
              own; the console shell behind the same route does not, so
              `AdminPanel` hangs the floating variant itself for its own
              states. This route is the one place the choice is made per-STATE
              rather than per-route. */}
        <Route
          path="/admin/:bookSlug?/:unit?"
          element={
            <Suspense fallback={<ChromelessFallback />}>
              <AdminPage />
            </Suspense>
          }
        />

        <Route element={<ChromeLayout />}>
          <Route path="/" element={<LibraryPage />} />
          <Route path="/about" element={<AboutPage />} />
          {/* The classroom offer (docs/plan/ela-classroom-extension.md).
                The teacher page is a public pitch and gates only its actions;
                `/classroom/:token` is what a teacher's purchasing
                decision-maker opens, and is public by construction — the token
                is the only thing standing in for a session there. */}
          <Route path="/for-teachers/odyssey" element={<TeacherPage />} />
          {/* The bare route predates the per-book path and may be printed or
                shared; keep it landing somewhere until it becomes an index. */}
          <Route
            path="/for-teachers"
            element={<Navigate to="/for-teachers/odyssey" replace />}
          />
          <Route path="/classroom/:token" element={<PurchaserPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          {/* A comp link from the console: the token is in the fragment,
                never the path (src/claim/claim-link.ts). */}
          <Route path="/claim" element={<ClaimPage />} />
          {/* checkout.sara.ai return target (plan 10 §3): confirms + unlocks.
                Serves BOTH lanes — `?kind=subscription` confirms a signup
                instead of a one-time order. */}
          <Route path="/purchase/complete" element={<PurchaseComplete />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </>
  );
}

/** Minimal not-found page, rendered under `ChromeLayout` like every other
 *  headerless route. */
function NotFound() {
  return (
    <main className="mx-auto flex min-h-svh max-w-2xl flex-col items-center justify-center gap-4 px-6 text-center">
      <p className="text-muted-foreground font-serif">Page not found.</p>
    </main>
  );
}

export default App;
